// src/components/ConflictCheckModal.js

import React, { useState, useEffect, useCallback } from 'react';

function ConflictCheckModal({
  show, // Boolean to control modal visibility
  onClose, // Function to close the modal
  conflictCheckResult, // The ConflictCheck object from the backend
  authAxios, // Axios instance for API calls
  onConflictResolved, // Callback function after conflict is resolved (e.g., to proceed with form submission)
  currentUser, // Current authenticated user for 'resolved_by'
  onEditConflictCheck, // Optional: Callback to update the parent's conflict check data if resolution happens here
  readOnly = false, // When true the modal shows details only and hides resolution actions
}) {
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(''); // 'WAIVED' or 'DECLINED'
  const [isResolving, setIsResolving] = useState(false);
  const [resolutionMessage, setResolutionMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (conflictCheckResult) {
      // Initialize resolution notes if they exist, otherwise clear
      setResolutionNotes(conflictCheckResult.resolution_notes || '');
      // For POTENTIAL we allow user to pick WAIVED/DECLINED; otherwise keep status
      setSelectedStatus(conflictCheckResult.status === 'POTENTIAL' ? conflictCheckResult.status : '');
    } else {
      setResolutionNotes('');
      setSelectedStatus('');
      setResolutionMessage({ type: '', text: '' });
    }
  }, [conflictCheckResult]);

  const handleResolveConflict = useCallback(async () => {
    if (!conflictCheckResult || !selectedStatus || !currentUser) {
      setResolutionMessage({ type: 'danger', text: 'Please select a status and ensure user is logged in.' });
      return;
    }

    if (selectedStatus === 'POTENTIAL') {
        setResolutionMessage({ type: 'danger', text: 'Please choose "Waived" or "Declined" to resolve.' });
        return;
    }
    // Require notes when waiving a conflict
    if (selectedStatus === 'WAIVED' && (!resolutionNotes || String(resolutionNotes).trim() === '')) {
      setResolutionMessage({ type: 'danger', text: 'Please provide a brief reason for waiving the conflict.' });
      setIsResolving(false);
      return;
    }

    setIsResolving(true);
    setResolutionMessage({ type: '', text: '' });

    try {
      const response = await authAxios.post(
        `conflict-checks/${conflictCheckResult.id}/resolve/`,
        {
          status: selectedStatus,
          resolution_notes: resolutionNotes,
        }
      );
      setResolutionMessage({ type: 'success', text: 'Conflict resolved successfully!' });
      // Update the parent component's conflictCheckResult or trigger a re-fetch
      if (onConflictResolved) {
        onConflictResolved(response.data); // Pass the updated ConflictCheck object
      }
      // Automatically close modal after a short delay for user to see success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error resolving conflict:', error.response ? error.response.data : error.message);
      setResolutionMessage({ type: 'danger', text: 'Failed to resolve conflict. Please try again.' });
    } finally {
      setIsResolving(false);
    }
  }, [conflictCheckResult, selectedStatus, resolutionNotes, authAxios, onConflictResolved, onClose, currentUser]);

  const getConflictBadgeColor = (status) => {
    switch (status) {
      case 'CLEAR': return 'bg-green-100 text-green-800';
      case 'POTENTIAL': return 'bg-yellow-100 text-yellow-800';
      case 'WAIVED': return 'bg-blue-100 text-blue-800';
      case 'DECLINED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!show || !conflictCheckResult) {
    return null;
  }

  const conflictsFound = conflictCheckResult.details?.conflicts_found || [];

  const handleContinueOnClear = () => {
    // For CLEAR results we simply notify parent and close modal so the flow can continue
    if (onConflictResolved) onConflictResolved(conflictCheckResult);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div className="relative mx-auto p-5 border w-full max-w-2xl max-h-[80vh] shadow-lg rounded-md bg-white overflow-y-auto">
        <div className="flex justify-between items-center pb-3 border-b">
          {conflictCheckResult.status === 'POTENTIAL' ? (
            <h5 className="text-2xl font-bold text-yellow-700">Potential Conflict of Interest</h5>
          ) : conflictCheckResult.status === 'CLEAR' ? (
            <h5 className="text-2xl font-bold text-green-700">Conflict Check — No Conflicts Found</h5>
          ) : conflictCheckResult.status === 'WAIVED' ? (
            <h5 className="text-2xl font-bold text-blue-700">Conflict Waived</h5>
          ) : (
            <h5 className="text-2xl font-bold text-red-700">Conflict Check Result</h5>
          )}
          <button onClick={onClose} className="text-gray-600 hover:text-gray-900">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="py-4">
          {resolutionMessage.text && (
            <div className={`p-4 mb-4 text-sm rounded-lg ${resolutionMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {resolutionMessage.text}
            </div>
          )}

          <p className="mb-4">
            Conflict check for <strong>"{conflictCheckResult.party_name_checked}"</strong> returned:
            <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getConflictBadgeColor(conflictCheckResult.status)}`}>{conflictCheckResult.status}</span>
          </p>

          {conflictCheckResult.status === 'POTENTIAL' && !readOnly && (
            <p className="mb-4 text-sm text-gray-700">
              The check identified one or more potential matches. You may <strong>Waive</strong> (provide a reason) to proceed with the creation, or <strong>Decline</strong> to stop the process.
              Waiving will create an audit-recorded exception and allow you to continue.
            </p>
          )}

          {conflictsFound.length > 0 ? (
            <>
              <h6 className="text-red-600 font-semibold mb-2">Details of Potential Conflicts:</h6>
              <ul className="space-y-3 mb-4">
                {conflictsFound.map((conflict, index) => (
                  <li key={index} className="bg-gray-50 p-3 rounded-md border">
                    <strong>Type:</strong> {conflict.type.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())} <br />
                    <strong>Match Source:</strong> {conflict.match_source.toUpperCase()} (ID: {conflict.source_id}) <br />
                    <strong>Source Name:</strong> {conflict.source_name} <br />
                    <strong>Matched Against:</strong> {conflict.matched_name} <br />
                    {conflict.score && <small className="text-gray-500">Similarity Score: {conflict.score}%</small>}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-green-600">No conflicts found for the specified parties.</p>
          )}

          {conflictCheckResult.status === 'POTENTIAL' && !readOnly && (
            <div className="p-4 bg-gray-100 rounded-lg mt-4">
              <h6 className="font-bold text-gray-800 mb-3">How would you like to proceed?</h6>
              <p className="text-sm text-gray-600 mb-3">Choose an action below and add a short note explaining the reason (required for waivers).</p>
              <div className="mb-4">
                <label htmlFor="resolutionStatus" className="block text-sm font-medium text-gray-700">Choose action:</label>
                <select
                  id="resolutionStatus"
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  required
                  disabled={isResolving}
                >
                  <option value="">Choose...</option>
                  <option value="WAIVED">Waive and proceed — provide reason</option>
                  <option value="DECLINED">Decline engagement — stop process</option>
                </select>
              </div>
              <div>
                <label htmlFor="resolutionNotes" className="block text-sm font-medium text-gray-700">Resolution Notes:</label>
                <textarea
                  id="resolutionNotes"
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  rows="3"
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Explain why you are waiving or declining (required for waivers)..."
                  disabled={isResolving}
                ></textarea>
              </div>
            </div>
          )}
          {(conflictCheckResult.status === 'WAIVED' || conflictCheckResult.status === 'DECLINED' || readOnly) && conflictCheckResult.resolution_notes && (
            <div className="p-4 bg-gray-50 rounded-md mt-4 border">
              <h6 className="font-semibold text-gray-800 mb-2">Resolution Notes</h6>
              <div className="text-sm text-gray-700 whitespace-pre-wrap">{conflictCheckResult.resolution_notes}</div>
            </div>
          )}
          {conflictCheckResult.status === 'CLEAR' && (
            <div className="p-3 bg-green-50 rounded-md mb-3">
              <p className="text-sm text-green-800">No potential conflicts were found. You may continue with the creation.</p>
            </div>
          )}
        </div>
        <div className="flex justify-end items-center pt-3 border-t space-x-4">
          <button
            type="button"
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            onClick={onClose}
            disabled={isResolving}
          >
            Close
          </button>
          {conflictCheckResult.status === 'POTENTIAL' && (selectedStatus === 'WAIVED' || selectedStatus === 'DECLINED') && (
            <button
              type="button"
              className={`bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isResolving ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handleResolveConflict}
              disabled={isResolving}
            >
              {isResolving ? 'Applying...' : 'Apply Resolution'}
            </button>
          )}

          {conflictCheckResult.status === 'CLEAR' && (
            <button
              type="button"
              className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              onClick={handleContinueOnClear}
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConflictCheckModal;
